import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from "react";
import {
  DeniedPersonRefusalError,
  HttpAuthClient,
  type AuthClient,
  type DeniedPersonRefusalReason,
  type StartActingAsRequest,
} from "./auth-client";
import { accessTokenManager } from "./token-manager";
import type { AuthenticatedSession, RoleClaim } from "@/types/clm-web";

export type AuthStatus =
  | "loading"
  | "authenticated"
  | "unauthenticated"
  | "refreshing"
  | "impersonating"
  | "returning"
  | "logout";

export interface GetAccessTokenOptions {
  /** Skip the in-memory token and have the auth service mint a new one. */
  forceRefresh?: boolean;
}

export interface RefreshSessionOptions {
  /**
   * Re-read the session without announcing it: `status` stays as it is (no "refreshing", so no
   * route cue and no header change), and a transient failure leaves it untouched. Everything
   * else is the same as a foreground refresh, including ending the session on an expired or
   * denied-person answer.
   */
  background?: boolean;
}

export interface AuthContextValue {
  status: AuthStatus;
  session: AuthenticatedSession | undefined;
  effectiveRoleClaims: RoleClaim[];
  actualUserRoleClaims: RoleClaim[];
  /**
   * Set when the auth service refused an access-token request because the Person is denied
   * (FR-019); the session is already ended. Cleared when a signed-in session is committed.
   */
  deniedPersonRefusal: DeniedPersonRefusalReason | undefined;
  client: AuthClient;
  refreshSession: (options?: RefreshSessionOptions) => Promise<void>;
  getAccessToken: (options?: GetAccessTokenOptions) => Promise<string | undefined>;
  logout: () => Promise<void>;
  startActingAs: (request: StartActingAsRequest) => Promise<void>;
  stopActingAs: () => Promise<void>;
}

export interface AuthProviderProps {
  children: ReactNode;
  client?: AuthClient;
  initialSession?: AuthenticatedSession;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({
  children,
  client,
  initialSession,
}: AuthProviderProps): ReactElement {
  const clientRef = useRef<AuthClient>(client ?? new HttpAuthClient());
  const [session, setSession] = useState<AuthenticatedSession | undefined>(initialSession);
  const [status, setStatus] = useState<AuthStatus>(() =>
    statusFromSession(initialSession, "loading"),
  );
  // The latest session commit/reset, available synchronously to pending operations.
  const committedSessionRef = useRef(initialSession);
  // The subject the committed session names; the in-memory bearer was minted for it.
  const committedSubRef = useRef(initialSession?.user?.sub);
  // Bumped on subject changes, logout or refusal, so outstanding token work cannot revive them.
  const tokenSubjectGenerationRef = useRef(0);
  // Bumped when a refusal ends the session, so a session read that began earlier cannot revive it.
  const refusalEpochRef = useRef(0);
  // Logout, denied-person end and acting-as boundaries invalidate earlier session work.
  const sessionChangeRef = useRef(0);
  // Refreshes take increasing tickets; only a newer committed answer supersedes an older one.
  const refreshTicketRef = useRef(0);
  const committedRefreshTicketRef = useRef(0);
  // Blocks token reads and session/status writes during logout, including the redirect wait.
  const logoutInProgressRef = useRef(false);
  // The refusal that ended the session, until a signed-in session replaces it. The ref answers
  // later token reads synchronously (no further request); the state tells the router.
  const deniedPersonRefusalRef = useRef<DeniedPersonRefusalReason | undefined>(undefined);
  const [deniedPersonRefusal, setDeniedPersonRefusal] = useState<
    DeniedPersonRefusalReason | undefined
  >(undefined);

  const commitSession = useCallback(
    (nextSession: AuthenticatedSession, fallbackStatus: AuthStatus) => {
      committedSubRef.current = nextSession.authenticated ? nextSession.user?.sub : undefined;
      if (nextSession.authenticated) {
        deniedPersonRefusalRef.current = undefined;
        setDeniedPersonRefusal(undefined);
      }
      committedSessionRef.current = nextSession.authenticated ? nextSession : undefined;
      setSession(committedSessionRef.current);
      setStatus(statusFromSession(nextSession, fallbackStatus));
    },
    [],
  );

  // Same end state as an ordinary 401 refresh: no bearer, no session.
  const endSessionForDeniedPerson = useCallback(() => {
    accessTokenManager.clear();
    tokenSubjectGenerationRef.current += 1;
    refusalEpochRef.current += 1;
    sessionChangeRef.current += 1;
    commitSession(
      { authenticated: false, roleClaims: [], sessionCookieManagedByAuthService: true },
      "unauthenticated",
    );
  }, [commitSession]);

  // Set when an explicit `refreshSession()` begins. The mount-time bootstrap
  // (`getCurrentSession()`) is then stale by definition: a late-landing
  // unauthenticated bootstrap must not clear the session the refresh committed
  // (e.g. the credential-recovery return page refreshes on mount).
  const refreshStartedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    const startEpoch = refusalEpochRef.current;
    const superseded = (): boolean =>
      cancelled || refreshStartedRef.current || startEpoch !== refusalEpochRef.current;

    async function loadSession(): Promise<void> {
      if (initialSession) {
        return;
      }

      setStatus("loading");
      const nextSession = await clientRef.current.getCurrentSession();

      if (!superseded()) {
        commitSession(nextSession, "authenticated");
      }
    }

    void loadSession().catch((error: unknown) => {
      if (superseded()) {
        return;
      }
      // Surface bootstrap failures instead of silently treating every user
      // as unauthenticated; an unexpected error here previously hid a
      // client-side fetch defect behind the normal logged-out experience.
      console.warn("Auth session bootstrap failed; treating user as unauthenticated.", error);
      committedSessionRef.current = undefined;
      setSession(undefined);
      setStatus("unauthenticated");
    });

    return () => {
      cancelled = true;
    };
  }, [commitSession, initialSession]);

  const refreshSession = useCallback(
    async (options?: RefreshSessionOptions) => {
      const background = options?.background === true;
      const epoch = refusalEpochRef.current;
      const change = sessionChangeRef.current;
      const ticket = (refreshTicketRef.current += 1);
      const superseded = (): boolean =>
        change !== sessionChangeRef.current ||
        logoutInProgressRef.current ||
        committedRefreshTicketRef.current > ticket;
      // Still set for a background read: the bootstrap is stale either way.
      refreshStartedRef.current = true;
      if (!background && !logoutInProgressRef.current) {
        setStatus("refreshing");
      }

      try {
        const nextSession = await clientRef.current.refreshSession();
        if (epoch !== refusalEpochRef.current) {
          // A refusal ended the session while this read was in flight: its answer predates the denial.
          if (deniedPersonRefusalRef.current) {
            throw new DeniedPersonRefusalError(deniedPersonRefusalRef.current);
          }
          return;
        }
        if (superseded()) {
          // A session change or newer committed refresh owns the session and status.
          return;
        }
        const nextSub = nextSession.authenticated ? nextSession.user?.sub : undefined;
        if (nextSub === undefined || nextSub !== committedSubRef.current) {
          // The cached bearer names the previous subject; the next read mints one for this one.
          accessTokenManager.clear();
          tokenSubjectGenerationRef.current += 1;
        }
        commitSession(nextSession, "authenticated");
        committedRefreshTicketRef.current = Math.max(committedRefreshTicketRef.current, ticket);
      } catch (error) {
        if (epoch !== refusalEpochRef.current) {
          // Superseded by a refusal: settle as that refusal, whatever this read's own failure was.
          if (deniedPersonRefusalRef.current) {
            throw new DeniedPersonRefusalError(deniedPersonRefusalRef.current);
          }
          throw error;
        }
        if (superseded()) {
          // Superseded: this read's failure is still the caller's to see, but it must not end or
          // reset what a later operation committed (a denied-person answer included; that
          // operation's own read decides the session).
          throw error;
        }
        if (error instanceof DeniedPersonRefusalError) {
          // Same end state as an ordinary 401 refresh, then the caller shows the refusal.
          endSessionForDeniedPerson();
          committedRefreshTicketRef.current = Math.max(committedRefreshTicketRef.current, ticket);
          throw error;
        }
        if (!background) {
          setStatus(statusFromSession(committedSessionRef.current, "authenticated"));
        }
        throw error;
      }
    },
    [commitSession, endSessionForDeniedPerson],
  );

  const getAccessToken = useCallback(
    async (options?: GetAccessTokenOptions): Promise<string | undefined> => {
      if (deniedPersonRefusalRef.current) {
        // The session is already ended; do not ask the auth service again.
        throw new DeniedPersonRefusalError(deniedPersonRefusalRef.current);
      }
      if (logoutInProgressRef.current) {
        return undefined;
      }

      const existingToken = accessTokenManager.getToken();
      if (!options?.forceRefresh && existingToken && !accessTokenManager.shouldRefresh()) {
        return existingToken;
      }

      const generation = tokenSubjectGenerationRef.current;
      const superseded = (): boolean =>
        generation !== tokenSubjectGenerationRef.current || logoutInProgressRef.current;
      setStatus("refreshing");

      try {
        const response = await clientRef.current.getAccessToken();
        if (superseded()) {
          // A subject change or session end invalidated this bearer.
          return undefined;
        }
        return accessTokenManager.setToken(response).accessToken;
      } catch (error) {
        // A refusal for a subject the session has since moved on from is not this session's.
        if (error instanceof DeniedPersonRefusalError && !superseded()) {
          // The router brings the user to the sign-in result for this code.
          deniedPersonRefusalRef.current = error.code;
          setDeniedPersonRefusal(error.code);
          endSessionForDeniedPerson();
        }
        throw error;
      } finally {
        // Superseded token work does not own the status.
        if (!superseded()) {
          setStatus(statusFromSession(committedSessionRef.current, "authenticated"));
        }
      }
    },
    [endSessionForDeniedPerson],
  );

  const logout = useCallback(async () => {
    sessionChangeRef.current += 1;
    logoutInProgressRef.current = true;
    tokenSubjectGenerationRef.current += 1;
    setStatus("logout");
    accessTokenManager.clear();
    let redirecting = false;
    try {
      const result = await clientRef.current.logout();
      if (
        result.logoutUrl &&
        typeof window !== "undefined" &&
        window.location.href !== result.logoutUrl
      ) {
        // A full-page navigation to the identity provider is in flight; keep
        // status "logout" so ProtectedRoute shows the sign-out interstitial
        // instead of flashing the login page. The next page load re-bootstraps
        // auth state from scratch. The flag is set only after assign() returns
        // so an exception still falls through to the unauthenticated reset.
        window.location.assign(result.logoutUrl);
        redirecting = true;
      }
    } finally {
      // A refresh that began during the sign-out request must not restore the session either.
      sessionChangeRef.current += 1;
      if (!redirecting) {
        logoutInProgressRef.current = false;
        committedSessionRef.current = undefined;
        setSession(undefined);
        setStatus("unauthenticated");
      }
    }
  }, []);

  const actingChangeSuperseded = useCallback(
    (epoch: number, change: number, startTicket: number): boolean =>
      logoutInProgressRef.current ||
      change !== sessionChangeRef.current ||
      epoch !== refusalEpochRef.current ||
      (committedRefreshTicketRef.current > startTicket &&
        committedSessionRef.current === undefined),
    [],
  );

  const startActingAs = useCallback(
    async (request: StartActingAsRequest) => {
      sessionChangeRef.current += 1;
      const epoch = refusalEpochRef.current;
      const change = sessionChangeRef.current;
      const startTicket = refreshTicketRef.current;
      if (!logoutInProgressRef.current) {
        setStatus("impersonating");
      }

      try {
        const nextSession = await clientRef.current.startActingAs(request);
        if (epoch !== refusalEpochRef.current && deniedPersonRefusalRef.current) {
          throw new DeniedPersonRefusalError(deniedPersonRefusalRef.current);
        }
        if (actingChangeSuperseded(epoch, change, startTicket)) {
          return;
        }
        sessionChangeRef.current += 1;
        commitSession(nextSession, "impersonating");
      } catch (error) {
        if (!actingChangeSuperseded(epoch, change, startTicket)) {
          setStatus(statusFromSession(committedSessionRef.current, "authenticated"));
        }
        throw error;
      }
    },
    [actingChangeSuperseded, commitSession],
  );

  const stopActingAs = useCallback(async () => {
    sessionChangeRef.current += 1;
    const epoch = refusalEpochRef.current;
    const change = sessionChangeRef.current;
    const startTicket = refreshTicketRef.current;
    if (!logoutInProgressRef.current) {
      setStatus("returning");
    }

    try {
      const nextSession = await clientRef.current.stopActingAs();
      if (epoch !== refusalEpochRef.current && deniedPersonRefusalRef.current) {
        throw new DeniedPersonRefusalError(deniedPersonRefusalRef.current);
      }
      if (actingChangeSuperseded(epoch, change, startTicket)) {
        return;
      }
      sessionChangeRef.current += 1;
      commitSession(nextSession, "authenticated");
    } catch (error) {
      if (!actingChangeSuperseded(epoch, change, startTicket)) {
        setStatus(statusFromSession(committedSessionRef.current, "authenticated"));
      }
      throw error;
    }
  }, [actingChangeSuperseded, commitSession]);

  const value = useMemo<AuthContextValue>(() => {
    const actingSession = session?.actingSession;
    const effectiveRoleClaims =
      actingSession?.status === "active"
        ? actingSession.effectiveUserRoles
        : (session?.roleClaims ?? []);
    const actualUserRoleClaims = actingSession?.actualUserRoles ?? session?.roleClaims ?? [];

    return {
      status,
      session,
      effectiveRoleClaims,
      actualUserRoleClaims,
      deniedPersonRefusal,
      client: clientRef.current,
      refreshSession,
      getAccessToken,
      logout,
      startActingAs,
      stopActingAs,
    };
  }, [
    deniedPersonRefusal,
    getAccessToken,
    logout,
    refreshSession,
    session,
    startActingAs,
    status,
    stopActingAs,
  ]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);

  if (!value) {
    throw new Error("Auth context is not available. Render within AuthProvider.");
  }

  return value;
}

function statusFromSession(
  session: AuthenticatedSession | undefined,
  authenticatedStatus: AuthStatus,
): AuthStatus {
  if (!session?.authenticated) {
    return session
      ? "unauthenticated"
      : authenticatedStatus === "loading"
        ? "loading"
        : "unauthenticated";
  }

  if (session.actingSession?.status === "active") {
    return "impersonating";
  }

  return authenticatedStatus === "impersonating" || authenticatedStatus === "returning"
    ? "authenticated"
    : authenticatedStatus;
}
